import {
  ACCOUNT_KIND_DEFS,
  DEAL_RESULT_DEFS,
  DEAL_STAGE_DEFS,
  LEAD_RATING_DEFS,
  LEAD_STATUS_DEFS,
} from './crm.js';
import { INVOICE_STATUS_DEFS, QUOTATION_STATUS_DEFS } from './sales.js';
import { EMPLOYEE_STATUS_DEFS } from './hr.js';
import { ASSET_STATUS_DEFS } from './assets.js';

/**
 * تصدير البيانات واستيرادها.
 *
 * الكتالوج هنا هو حدّ الأمان كله: أسماء الجداول والأعمدة تُبنى منه لا من
 * مدخلات المستخدم — بنفس مبدأ محرّك التقارير. ما ليس في هذه القائمة لا
 * يُصدَّر ولا يُستورَد مهما أُرسل في الطلب.
 */

/**
 * USER: بريد عضو في الشركة يُترجَم إلى معرّفه عند الاستيراد. البريد لا الاسم:
 * الاسم يتكرّر ويُكتب بأكثر من صيغة، والبريد فريد ويعرفه من يجهّز الملف.
 */
export type FieldKind =
  | 'TEXT'
  | 'NUMBER'
  | 'MONEY'
  | 'DATE'
  | 'BOOL'
  | 'ENUM'
  | 'REF'
  | 'PHONE'
  | 'USER';

export interface ExportColumn {
  /** اسم العمود في قاعدة البيانات. */
  key: string;
  /** العنوان في الملف — عربي، فهو ما يقرأه المستخدم في إكسل. */
  label: string;
  kind: FieldKind;
  /** ترجمة قيم التعداد إلى العربية. */
  labels?: Readonly<Record<string, string>>;
  /** مرجع إلى جدول آخر: يُصدَّر اسمه لا معرّفه. */
  ref?: { relation: string; field: string };
  /**
   * عمود مشتقّ من حقل مخصَّص يعرّفه المستأجر.
   *
   * قيمته داخل عمود JSON لا في عمود مستقل، فتُقرأ وتُكتب بمسار آخر.
   */
  custom?: boolean;
}

export interface ImportColumn extends ExportColumn {
  required?: boolean;
  /** قيمة افتراضية حين لا يُذكر العمود. */
  fallback?: string | number | boolean;
  /** أمثلة تُكتب في القالب لتوضيح الصيغة. */
  example?: string;
}

export interface DataResource {
  key: string;
  label: string;
  description: string;
  /** اسم النموذج في Prisma. */
  model: string;
  /** أدنى دور يصدّر. */
  minRole: 'EMPLOYEE' | 'SALES' | 'MANAGER' | 'ADMIN';
  columns: readonly ExportColumn[];
  /** أعمدة الاستيراد؛ غيابها يعني أن المورد يُصدَّر ولا يُستورَد. */
  importColumns?: readonly ImportColumn[];
  /** حقل يمنع تكرار الاستيراد — يُبحث به قبل الإنشاء. */
  importUniqueBy?: string;
}

/* ==========================================================================
   ترجمات التعدادات
   ========================================================================== */

/**
 * الترجمات تُشتق من تعريفات الوحدات لا تُكتب هنا.
 *
 * كتابتها يدويًا تفترق عن الحقيقة عند أول تعديل: كُتبت WON وLEAD الحالة
 * اسمها CONVERTED، فخرج المفتاح الإنجليزي في ملف يقرؤه محاسب. الاشتقاق
 * يجعل ذلك مستحيلًا.
 */
function labelsOf<K extends string>(
  defs: readonly { key: K; label: string }[],
): Readonly<Record<string, string>> {
  return Object.fromEntries(defs.map((d) => [d.key, d.label]));
}

const LEAD_STATUS = labelsOf(LEAD_STATUS_DEFS);
const LEAD_RATING = labelsOf(LEAD_RATING_DEFS);
const ACCOUNT_KIND = labelsOf(ACCOUNT_KIND_DEFS);
const DEAL_STAGE = labelsOf(DEAL_STAGE_DEFS);
const DEAL_RESULT = labelsOf(DEAL_RESULT_DEFS);
const INVOICE_STATUS = labelsOf(INVOICE_STATUS_DEFS);
const QUOTATION_STATUS = labelsOf(QUOTATION_STATUS_DEFS);
const EMPLOYEE_STATUS = labelsOf(EMPLOYEE_STATUS_DEFS);
const ASSET_STATUS = labelsOf(ASSET_STATUS_DEFS);

/* ==========================================================================
   الموارد
   ========================================================================== */

export const DATA_RESOURCES: readonly DataResource[] = [
  {
    key: 'leads',
    label: 'العملاء المحتملون',
    description: 'قاعدة العملاء المحتملين ومصادرهم وحالاتهم.',
    model: 'lead',
    minRole: 'SALES',
    columns: [
      { key: 'name', label: 'الاسم', kind: 'TEXT' },
      { key: 'company', label: 'الشركة', kind: 'TEXT' },
      { key: 'jobTitle', label: 'المسمى الوظيفي', kind: 'TEXT' },
      { key: 'email', label: 'البريد الإلكتروني', kind: 'TEXT' },
      { key: 'phone', label: 'الهاتف', kind: 'PHONE' },
      { key: 'status', label: 'الحالة', kind: 'ENUM', labels: LEAD_STATUS },
      { key: 'rating', label: 'الأولوية', kind: 'ENUM', labels: LEAD_RATING },
      { key: 'source', label: 'المصدر', kind: 'TEXT' },
      { key: 'industry', label: 'النشاط', kind: 'TEXT' },
      { key: 'city', label: 'المدينة', kind: 'TEXT' },
      { key: 'value', label: 'القيمة التقديرية', kind: 'MONEY' },
      { key: 'notes', label: 'ملاحظات', kind: 'TEXT' },
      { key: 'createdAt', label: 'تاريخ التسجيل', kind: 'DATE' },
    ],
    importColumns: [
      { key: 'name', label: 'الاسم', kind: 'TEXT', required: true, example: 'أحمد محمود' },
      { key: 'company', label: 'الشركة', kind: 'TEXT', example: 'شركة النور' },
      { key: 'jobTitle', label: 'المسمى الوظيفي', kind: 'TEXT', example: 'مدير تسويق' },
      { key: 'email', label: 'البريد الإلكتروني', kind: 'TEXT', example: 'ahmed@example.com' },
      { key: 'phone', label: 'الهاتف', kind: 'PHONE', example: '01012345678' },
      {
        key: 'status',
        label: 'الحالة',
        kind: 'ENUM',
        labels: LEAD_STATUS,
        fallback: 'NEW',
        example: 'جديد',
      },
      { key: 'rating', label: 'الأولوية', kind: 'ENUM', labels: LEAD_RATING, example: 'أولوية متوسطة' },
      { key: 'source', label: 'المصدر', kind: 'TEXT', example: 'فيسبوك' },
      { key: 'industry', label: 'النشاط', kind: 'TEXT', example: 'عقارات' },
      { key: 'city', label: 'المدينة', kind: 'TEXT', example: 'القاهرة' },
      { key: 'value', label: 'القيمة التقديرية', kind: 'MONEY', example: '50000' },
      { key: 'notes', label: 'ملاحظات', kind: 'TEXT' },
      /*
       * المسؤول وتاريخ التسجيل: لنقل قاعدة عملاء من نظام سابق كما هي.
       * بدونهما يصير كل عميل منقول ملكَ من استورده ومسجَّلًا يوم الاستيراد،
       * فتفقد تقارير المندوبين وأعمار العملاء معناها. المسؤول الفارغ هو
       * المستورِد — كما في الإضافة من النموذج.
       */
      { key: 'ownerId', label: 'المسؤول', kind: 'USER', example: 'sara@example.com' },
      { key: 'createdAt', label: 'تاريخ التسجيل', kind: 'DATE', example: '2026-01-31' },
    ],
    importUniqueBy: 'phone',
  },

  {
    key: 'accounts',
    label: 'العملاء',
    description: 'قاعدة العملاء وبياناتهم.',
    model: 'account',
    minRole: 'SALES',
    columns: [
      { key: 'name', label: 'اسم العميل', kind: 'TEXT' },
      { key: 'legalName', label: 'الاسم القانوني', kind: 'TEXT' },
      { key: 'kind', label: 'النوع', kind: 'ENUM', labels: ACCOUNT_KIND },
      { key: 'email', label: 'البريد الإلكتروني', kind: 'TEXT' },
      { key: 'phone', label: 'الهاتف', kind: 'PHONE' },
      { key: 'taxNumber', label: 'الرقم الضريبي', kind: 'TEXT' },
      { key: 'industry', label: 'النشاط', kind: 'TEXT' },
      { key: 'city', label: 'المدينة', kind: 'TEXT' },
      { key: 'address', label: 'العنوان', kind: 'TEXT' },
      { key: 'website', label: 'الموقع', kind: 'TEXT' },
      { key: 'isActive', label: 'نشط', kind: 'BOOL' },
      { key: 'createdAt', label: 'تاريخ الإضافة', kind: 'DATE' },
    ],
    importColumns: [
      { key: 'name', label: 'اسم العميل', kind: 'TEXT', required: true, example: 'شركة النور' },
      { key: 'legalName', label: 'الاسم القانوني', kind: 'TEXT' },
      {
        key: 'kind',
        label: 'النوع',
        kind: 'ENUM',
        labels: ACCOUNT_KIND,
        fallback: 'COMPANY',
        example: 'شركة',
      },
      { key: 'email', label: 'البريد الإلكتروني', kind: 'TEXT', example: 'info@example.com' },
      { key: 'phone', label: 'الهاتف', kind: 'PHONE', example: '0221234567' },
      { key: 'taxNumber', label: 'الرقم الضريبي', kind: 'TEXT', example: '123-456-789' },
      { key: 'industry', label: 'النشاط', kind: 'TEXT', example: 'عقارات' },
      { key: 'city', label: 'المدينة', kind: 'TEXT', example: 'القاهرة' },
      { key: 'address', label: 'العنوان', kind: 'TEXT' },
      { key: 'website', label: 'الموقع', kind: 'TEXT' },
    ],
    importUniqueBy: 'name',
  },

  {
    key: 'contacts',
    label: 'جهات الاتصال',
    description: 'الأشخاص داخل حسابات العملاء.',
    model: 'contact',
    minRole: 'SALES',
    columns: [
      { key: 'name', label: 'الاسم', kind: 'TEXT' },
      { key: 'jobTitle', label: 'المسمى الوظيفي', kind: 'TEXT' },
      { key: 'email', label: 'البريد الإلكتروني', kind: 'TEXT' },
      { key: 'phone', label: 'الهاتف', kind: 'PHONE' },
      { key: 'isPrimary', label: 'جهة رئيسية', kind: 'BOOL' },
      {
        key: 'accountId',
        label: 'العميل',
        kind: 'REF',
        ref: { relation: 'account', field: 'name' },
      },
    ],
  },

  {
    key: 'deals',
    label: 'الصفقات',
    description: 'خط الأنابيب وقيمه ونتائجه.',
    model: 'deal',
    minRole: 'SALES',
    columns: [
      { key: 'title', label: 'الصفقة', kind: 'TEXT' },
      { key: 'stage', label: 'المرحلة', kind: 'ENUM', labels: DEAL_STAGE },
      { key: 'result', label: 'النتيجة', kind: 'ENUM', labels: DEAL_RESULT },
      { key: 'value', label: 'القيمة', kind: 'MONEY' },
      { key: 'probability', label: 'الاحتمالية', kind: 'NUMBER' },
      { key: 'source', label: 'المصدر', kind: 'TEXT' },
      { key: 'lostReason', label: 'سبب الخسارة', kind: 'TEXT' },
      { key: 'expectedCloseAt', label: 'الإغلاق المتوقع', kind: 'DATE' },
      { key: 'closedAt', label: 'تاريخ الإغلاق', kind: 'DATE' },
      {
        key: 'accountId',
        label: 'العميل',
        kind: 'REF',
        ref: { relation: 'account', field: 'name' },
      },
      { key: 'createdAt', label: 'تاريخ الإنشاء', kind: 'DATE' },
    ],
  },

  {
    key: 'quotations',
    label: 'عروض الأسعار',
    description: 'العروض الصادرة وحالاتها.',
    model: 'quotation',
    minRole: 'SALES',
    columns: [
      { key: 'number', label: 'رقم العرض', kind: 'TEXT' },
      { key: 'title', label: 'العنوان', kind: 'TEXT' },
      { key: 'status', label: 'الحالة', kind: 'ENUM', labels: QUOTATION_STATUS },
      { key: 'subtotal', label: 'المجموع', kind: 'MONEY' },
      { key: 'discount', label: 'الخصم', kind: 'MONEY' },
      { key: 'taxAmount', label: 'الضريبة', kind: 'MONEY' },
      { key: 'total', label: 'الإجمالي', kind: 'MONEY' },
      { key: 'issuedAt', label: 'تاريخ الإصدار', kind: 'DATE' },
      { key: 'validUntil', label: 'صالح حتى', kind: 'DATE' },
      {
        key: 'accountId',
        label: 'العميل',
        kind: 'REF',
        ref: { relation: 'account', field: 'name' },
      },
    ],
  },

  {
    key: 'invoices',
    label: 'الفواتير',
    description: 'الفواتير الصادرة وما حُصّل منها.',
    model: 'invoice',
    minRole: 'MANAGER',
    columns: [
      { key: 'number', label: 'رقم الفاتورة', kind: 'TEXT' },
      { key: 'title', label: 'البيان', kind: 'TEXT' },
      { key: 'status', label: 'الحالة', kind: 'ENUM', labels: INVOICE_STATUS },
      { key: 'subtotal', label: 'المجموع', kind: 'MONEY' },
      { key: 'discount', label: 'الخصم', kind: 'MONEY' },
      { key: 'taxAmount', label: 'الضريبة', kind: 'MONEY' },
      { key: 'total', label: 'الإجمالي', kind: 'MONEY' },
      { key: 'paidAmount', label: 'المحصَّل', kind: 'MONEY' },
      { key: 'issuedAt', label: 'تاريخ الإصدار', kind: 'DATE' },
      { key: 'dueAt', label: 'تاريخ الاستحقاق', kind: 'DATE' },
      { key: 'paidAt', label: 'تاريخ السداد', kind: 'DATE' },
      {
        key: 'accountId',
        label: 'العميل',
        kind: 'REF',
        ref: { relation: 'account', field: 'name' },
      },
    ],
  },

  {
    key: 'payments',
    label: 'التحصيلات',
    description: 'ما حُصِّل من العملاء.',
    model: 'payment',
    minRole: 'MANAGER',
    columns: [
      { key: 'number', label: 'رقم السند', kind: 'TEXT' },
      { key: 'amount', label: 'المبلغ', kind: 'MONEY' },
      { key: 'method', label: 'طريقة السداد', kind: 'TEXT' },
      { key: 'reference', label: 'المرجع', kind: 'TEXT' },
      { key: 'paidAt', label: 'تاريخ التحصيل', kind: 'DATE' },
      {
        key: 'invoiceId',
        label: 'الفاتورة',
        kind: 'REF',
        ref: { relation: 'invoice', field: 'number' },
      },
    ],
  },

  {
    key: 'services',
    label: 'كتالوج الخدمات',
    description: 'الخدمات وأسعارها.',
    model: 'serviceItem',
    minRole: 'SALES',
    columns: [
      { key: 'name', label: 'الخدمة', kind: 'TEXT' },
      { key: 'description', label: 'الوصف', kind: 'TEXT' },
      { key: 'unit', label: 'الوحدة', kind: 'TEXT' },
      { key: 'unitPrice', label: 'سعر الوحدة', kind: 'MONEY' },
      { key: 'category', label: 'التصنيف', kind: 'TEXT' },
      { key: 'isActive', label: 'نشط', kind: 'BOOL' },
    ],
    importColumns: [
      { key: 'name', label: 'الخدمة', kind: 'TEXT', required: true, example: 'إدارة حسابات' },
      { key: 'description', label: 'الوصف', kind: 'TEXT' },
      { key: 'unit', label: 'الوحدة', kind: 'TEXT', fallback: 'خدمة', example: 'شهر' },
      {
        key: 'unitPrice',
        label: 'سعر الوحدة',
        kind: 'MONEY',
        required: true,
        example: '5000',
      },
      { key: 'category', label: 'التصنيف', kind: 'TEXT', example: 'تسويق رقمي' },
    ],
    importUniqueBy: 'name',
  },

  {
    key: 'employees',
    label: 'الموظفون',
    description: 'ملفات الموظفين ورواتبهم.',
    model: 'employee',
    minRole: 'ADMIN',
    columns: [
      { key: 'code', label: 'الكود', kind: 'TEXT' },
      { key: 'name', label: 'الاسم', kind: 'TEXT' },
      { key: 'jobTitle', label: 'المسمى الوظيفي', kind: 'TEXT' },
      { key: 'department', label: 'القسم', kind: 'TEXT' },
      { key: 'email', label: 'البريد الإلكتروني', kind: 'TEXT' },
      { key: 'phone', label: 'الهاتف', kind: 'PHONE' },
      { key: 'hireDate', label: 'تاريخ التعيين', kind: 'DATE' },
      { key: 'status', label: 'الحالة', kind: 'ENUM', labels: EMPLOYEE_STATUS },
      { key: 'baseSalary', label: 'الراتب الأساسي', kind: 'MONEY' },
      { key: 'annualLeaveBalance', label: 'رصيد الإجازة', kind: 'NUMBER' },
    ],
    importColumns: [
      { key: 'name', label: 'الاسم', kind: 'TEXT', required: true, example: 'محمد عبد الله' },
      { key: 'code', label: 'الكود', kind: 'TEXT', example: 'EMP-001' },
      { key: 'jobTitle', label: 'المسمى الوظيفي', kind: 'TEXT', example: 'محاسب' },
      { key: 'department', label: 'القسم', kind: 'TEXT', example: 'المالية' },
      { key: 'email', label: 'البريد الإلكتروني', kind: 'TEXT' },
      { key: 'phone', label: 'الهاتف', kind: 'PHONE' },
      {
        key: 'hireDate',
        label: 'تاريخ التعيين',
        kind: 'DATE',
        required: true,
        example: '2024-01-15',
      },
      { key: 'baseSalary', label: 'الراتب الأساسي', kind: 'MONEY', example: '8000' },
      {
        key: 'annualLeaveBalance',
        label: 'رصيد الإجازة',
        kind: 'NUMBER',
        fallback: 21,
        example: '21',
      },
    ],
    importUniqueBy: 'code',
  },

  {
    key: 'assets',
    label: 'الأصول الثابتة',
    description: 'سجل الأصول وقيمها الدفترية.',
    model: 'asset',
    minRole: 'MANAGER',
    columns: [
      { key: 'code', label: 'الكود', kind: 'TEXT' },
      { key: 'name', label: 'الأصل', kind: 'TEXT' },
      { key: 'serialNumber', label: 'الرقم التسلسلي', kind: 'TEXT' },
      { key: 'location', label: 'الموقع', kind: 'TEXT' },
      { key: 'status', label: 'الحالة', kind: 'ENUM', labels: ASSET_STATUS },
      { key: 'purchaseDate', label: 'تاريخ الشراء', kind: 'DATE' },
      { key: 'purchaseCost', label: 'تكلفة الاقتناء', kind: 'MONEY' },
      { key: 'salvageValue', label: 'قيمة الخردة', kind: 'MONEY' },
      { key: 'accumulatedDepreciation', label: 'مجمع الإهلاك', kind: 'MONEY' },
      {
        key: 'categoryId',
        label: 'التصنيف',
        kind: 'REF',
        ref: { relation: 'category', field: 'name' },
      },
      {
        key: 'custodianId',
        label: 'في عهدة',
        kind: 'REF',
        ref: { relation: 'custodian', field: 'name' },
      },
    ],
  },

  {
    key: 'expenses',
    label: 'المصروفات',
    description: 'المصروفات المسجَّلة.',
    model: 'expense',
    minRole: 'MANAGER',
    columns: [
      { key: 'number', label: 'رقم الإذن', kind: 'TEXT' },
      { key: 'description', label: 'البيان', kind: 'TEXT' },
      { key: 'amount', label: 'المبلغ', kind: 'MONEY' },
      { key: 'taxAmount', label: 'الضريبة', kind: 'MONEY' },
      { key: 'date', label: 'التاريخ', kind: 'DATE' },
      { key: 'reference', label: 'المرجع', kind: 'TEXT' },
    ],
  },
] as const;

export const DATA_RESOURCE_MAP = Object.fromEntries(
  DATA_RESOURCES.map((r) => [r.key, r]),
) as Readonly<Record<string, DataResource>>;

/** ما يمكن استيراده — جزء مما يمكن تصديره. */
export const IMPORTABLE = DATA_RESOURCES.filter((r) => r.importColumns?.length);

/* ==========================================================================
   CSV
   ========================================================================== */

/**
 * علامة ترتيب البايتات في أول الملف.
 *
 * بدونها يفتح إكسل ملف UTF-8 بترميز النظام المحلي فتظهر العربية طلاسم —
 * وهو أول ما يراه المستخدم فيظن التصدير معطوبًا. ثلاثة بايتات تحسم الأمر.
 */
export const UTF8_BOM = '﻿';

/**
 * تهريب خلية CSV.
 *
 * الاقتباس يقع حين تحوي الخلية فاصلة أو سطرًا جديدًا أو علامة اقتباس،
 * والاقتباس داخلها يُضاعف. وهذا هو معيار RFC 4180.
 */
/**
 * قيمة يكتبها النظام لا المستخدم.
 *
 * حارس الحقن يفترض أن كل خلية قد تكون نصًّا كتبه مستخدم بخبث، وهو افتراض
 * صحيح إلا فيما يولّده النظام نفسه من قيمة مُوحَّدة سلفًا. وبلا هذا الاستثناء
 * لا سبيل إلى كتابة خلية يفهمها إكسل نصًّا، فيعود يفسد أرقام الهواتف.
 *
 * الاقتباس يبقى ساريًا عليها: الاستثناء من الحارس وحده لا من قواعد CSV.
 */
export class CsvLiteral {
  constructor(readonly text: string) {}
}

export function csvLiteral(text: string): CsvLiteral {
  return new CsvLiteral(text);
}

export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';

  if (value instanceof CsvLiteral) {
    return /[",\n\r]/.test(value.text)
      ? `"${value.text.replace(/"/g, '""')}"`
      : value.text;
  }

  const s = String(value);

  /*
   * الحماية من حقن الصيغ.
   *
   * خلية تبدأ بـ= أو + أو - أو @ يفسّرها إكسل صيغةً لا نصًا، فتُنفَّذ عند
   * فتح الملف. اسم عميل كُتب بخبث يصير أمرًا يعمل على جهاز من فتحه.
   * البادئة بفاصلة عليا تُبطل ذلك وتبقى القيمة مقروءة.
   */
  const guarded = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;

  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

export function toCsv(headers: readonly string[], rows: readonly unknown[][]): string {
  const lines = [headers.map(csvCell).join(',')];
  for (const row of rows) lines.push(row.map(csvCell).join(','));
  // نهايات أسطر ويندوز: إكسل على ويندوز هو المستهلك الأول.
  return UTF8_BOM + lines.join('\r\n');
}

/**
 * تحليل CSV.
 *
 * مكتوب هنا لا مُستورَد: المكتبات الجاهزة تجرّ اعتماديات لملف من عمودين،
 * والقواعد التي تهمّ ثلاث — الاقتباس، ومضاعفته داخل المقتبَس، والسطر
 * الجديد داخل الخلية المقتبَسة.
 */
export function parseCsv(text: string): string[][] {
  const clean = text.replace(/^﻿/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < clean.length; i++) {
    const c = clean[i]!;

    if (quoted) {
      if (c === '"') {
        if (clean[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += c;
      continue;
    }

    if (c === '"') {
      quoted = true;
    } else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && clean[i + 1] === '\n') i++;
      row.push(cell);
      // السطر الفارغ يُتخطّى: ملفات إكسل تنتهي بسطر فارغ عادةً.
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
      cell = '';
    } else cell += c;
  }

  if (cell !== '' || row.length) {
    row.push(cell);
    if (row.some((v) => v.trim() !== '')) rows.push(row);
  }

  return rows;
}

/** أقصى عدد صفوف في ملف استيراد واحد. */
export const MAX_IMPORT_ROWS = 2000;

/**
 * تحويل الأرقام العربية إلى لاتينية.
 *
 * من يكتب في إكسل بلوحة عربية قد يُدخل ٠١٢٣، وparseFloat لا يفهمها فتصير
 * الأسعار أصفارًا بصمت.
 */
export function normalizeDigits(value: string): string {
  return value
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
}
